/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {

    constructor() {
      super();
    }
    connectedCallback() {
      setTimeout(() => {
        this.target = this.dataset.target;
        this.buttons = Array.from(this.querySelectorAll('button'));
        this.setupProgressBar();
        this.setupCarouselBounds();
        this.setupCarouselHeight();
        this.buttons.forEach((button, i) => {
          button.addEventListener('click', (event) => {
            let handle = button.dataset.collection;
            [].forEach.call(this.buttons, function(el) {
              el.classList.remove('active');
            });

            button.classList.add('active');

            if (handle) {
              this.toggleCollection(handle);
            }

            event.preventDefault();
          });
        });
      }, 20);
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          let index = this.buttons.indexOf(event.target);

          this.buttons[index].dispatchEvent(new Event('click'));
        });
      }
    }
    setupProgressBar() {
      const slider = document.getElementById(this.target);
      if (!slider) return;

      const progressBar = slider.parentNode.querySelector('.flickity-progress--bar');
      if (!progressBar) return;

      if (this.progressBarBound) {
        window.requestAnimationFrame(() => {
          if (this.updateProgressBar) this.updateProgressBar(0);
        });
        return;
      }

      const bindProgress = () => {
        const flkty = Flickity.data(slider);
        if (!flkty) {
          window.requestAnimationFrame(bindProgress);
          return;
        }

        const getVisibleCells = () => {
          const firstCell = flkty.cells.find((cell) => cell.element.classList.contains('carousel__slide'));
          const cellWidth = firstCell ? firstCell.size.outerWidth : 0;

          if (!cellWidth) return 1;

          return Math.max(1, Math.round(slider.clientWidth / cellWidth));
        };

        const updateProgress = (scrollProgress) => {
          const totalCells = flkty.cells.length;
          if (!totalCells) return;

          const visibleCells = Math.min(getVisibleCells(), totalCells);
          const normalizedProgress = typeof scrollProgress === 'number'
            ? Math.max(0, Math.min(1, scrollProgress))
            : Math.min(flkty.selectedIndex / Math.max(totalCells - visibleCells, 1), 1);
          const progressWidth = ((visibleCells + ((totalCells - visibleCells) * normalizedProgress)) / totalCells) * 100;

          progressBar.style.width = `${Math.min(progressWidth, 100)}%`;
        };

        updateProgress(0);
        this.progressBarBound = true;
        this.updateProgressBar = updateProgress;
        flkty.on('scroll', updateProgress);
        flkty.on('select', () => updateProgress());
        flkty.on('resize', () => updateProgress());
      };

      bindProgress();
    }
    setupCarouselHeight() {
      const slider = document.getElementById(this.target);
      if (!slider) return;

      if (this.carouselHeightBound) {
        if (this.observeCarouselHeight) this.observeCarouselHeight();
        return;
      }

      const bindHeight = () => {
        const flkty = Flickity.data(slider);
        if (!flkty) {
          window.requestAnimationFrame(bindHeight);
          return;
        }

        let resizeFrame;
        const scheduleResize = () => {
          window.cancelAnimationFrame(resizeFrame);
          resizeFrame = window.requestAnimationFrame(() => {
            flkty.resize();

            if (this.updateProgressBar) this.updateProgressBar();
          });
        };

        this.observeCarouselHeight = () => {
          if (this.carouselHeightObserver) {
            this.carouselHeightObserver.disconnect();
          }

          if ('ResizeObserver' in window) {
            this.carouselHeightObserver = new ResizeObserver(scheduleResize);
            slider.querySelectorAll('.carousel__slide').forEach((slide) => {
              this.carouselHeightObserver.observe(slide);
            });
          }

          scheduleResize();
          window.setTimeout(scheduleResize, 300);
          window.setTimeout(scheduleResize, 1000);
        };

        this.carouselHeightBound = true;
        this.observeCarouselHeight();
        window.addEventListener('resize', scheduleResize);
      };

      bindHeight();
    }
    setupCarouselBounds() {
      const slider = document.getElementById(this.target);
      if (!slider || this.carouselBoundsBound) return;

      const bindBounds = () => {
        const flkty = Flickity.data(slider);
        if (!flkty) {
          window.requestAnimationFrame(bindBounds);
          return;
        }

        const getVisibleCells = () => {
          const firstCell = flkty.cells.find((cell) => cell.element.classList.contains('carousel__slide'));
          const cellWidth = firstCell ? firstCell.size.outerWidth : 0;

          if (!cellWidth) return 1;

          return Math.max(1, Math.round(slider.clientWidth / cellWidth));
        };

        const getMaxSelectableIndex = () => {
          const visibleCells = Math.min(getVisibleCells(), flkty.cells.length);
          return Math.max(0, flkty.cells.length - visibleCells);
        };

        const originalSelect = flkty.select.bind(flkty);
        const originalSelectCell = flkty.selectCell.bind(flkty);
        const prevButton = slider.querySelector('.flickity-prev');
        const nextButton = slider.querySelector('.flickity-next');

        const setNavDisabled = (button, disabled) => {
          if (!button) return;

          button.classList.toggle('is-disabled', disabled);
          button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          button.setAttribute('tabindex', disabled ? '-1' : '0');
        };

        const updateNavState = () => {
          const maxSelectableIndex = getMaxSelectableIndex();
          const canScroll = flkty.cells.length > getVisibleCells();

          setNavDisabled(prevButton, !canScroll || flkty.selectedIndex <= 0);
          setNavDisabled(nextButton, !canScroll || flkty.selectedIndex >= maxSelectableIndex);
        };

        const stopDisabledNav = (event) => {
          if (!event.currentTarget.classList.contains('is-disabled')) return;

          event.preventDefault();
          event.stopImmediatePropagation();
        };

        prevButton?.addEventListener('click', stopDisabledNav, true);
        prevButton?.addEventListener('keyup', stopDisabledNav, true);
        nextButton?.addEventListener('click', stopDisabledNav, true);
        nextButton?.addEventListener('keyup', stopDisabledNav, true);

        flkty.select = (index, isWrap, isInstant) => {
          originalSelect(Math.min(index, getMaxSelectableIndex()), false, isInstant);
          updateNavState();
        };

        flkty.selectCell = (value, isWrap, isInstant) => {
          originalSelectCell(value, false, isInstant);

          if (flkty.selectedIndex > getMaxSelectableIndex()) {
            originalSelect(getMaxSelectableIndex(), false, true);
          }

          updateNavState();
        };

        flkty.on('select', () => {
          const maxSelectableIndex = getMaxSelectableIndex();

          if (flkty.selectedIndex > maxSelectableIndex) {
            originalSelect(maxSelectableIndex, false, true);
          }

          updateNavState();
        });

        flkty.on('resize', () => {
          if (flkty.selectedIndex > getMaxSelectableIndex()) {
            originalSelect(getMaxSelectableIndex(), false, true);
          }

          updateNavState();
        });

        updateNavState();
        this.carouselBoundsBound = true;
      };

      bindBounds();
    }
    toggleCollection(handle) {
      let slider = document.getElementById(this.target),
        products = slider.querySelectorAll(`.columns:not([data-collection*="${handle}"])`),
        active_products = slider.querySelectorAll(`[data-collection*="${handle}"]`),
        flkty = Flickity.data(slider);
      [].forEach.call(products, function(el) {
        el.classList.remove('carousel__slide');
        slider.append(el);
      });
      [].forEach.call(active_products, function(el) {
        if (el.dataset.collection === handle) {
          el.classList.add('carousel__slide');
        } else {
          el.classList.remove('carousel__slide');
          slider.append(el);
        }
      });
      flkty.insert(active_products);
      flkty.reloadCells();
      flkty.select(0, 0, 1);
      this.setupProgressBar();
      this.setupCarouselBounds();
      this.setupCarouselHeight();
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}
